import { defineComponent, computed, inject } from 'vue';
import { isFunction, isString, isObject } from 'lodash-es';
import { Hover } from '../shared';
import config from '../config';
import props from './grid-item-props';
import { useTNodeJSX } from '../hooks/tnode';
import TImage from '../image';
import TBadge from '../badge';
import { usePrefixClass } from '../hooks/useClass';

const { prefix } = config;

export default defineComponent({
  name: `${prefix}-grid-item`,
  directives: { Hover },
  components: { TImage, TBadge },
  props,
  setup(props, context) {
    const gridItemClass = usePrefixClass('grid-item');

    const renderTNodeJSX = useTNodeJSX();
    const { column, border, align, gutter, hover } = inject<any>('grid');

    const rootStyle = computed(() => {
      const percent = column.value > 0 ? `${100 / +column.value}%` : 0;
      const style: Record<string, any> = {
        textAlign: ['center', 'left'].includes(align.value) ? align.value : 'center',
      };
      if (percent !== 0) {
        style.flexBasis = percent;
      }
      return style;
    });

    const size = computed(() => {
      const col = column.value;
      if (!col || col === 4) return 'middle';
      return col > 4 ? 'small' : 'large';
    });

    const realImage = computed(() => {
      if (isString(props.image)) return { src: props.image };
      if (isObject(props.image) && !isFunction(props.image) && !context.slots.image) {
        return props.image;
      }
      return null;
    });

    const gridItemClasses = computed(() => [
      `${gridItemClass.value}`,
      `${gridItemClass.value}--${props.layout}`,
      {
        [`${gridItemClass.value}--bordered`]: border.value,
        [`${gridItemClass.value}--surround`]: border.value && gutter.value,
      },
    ]);

    return () => {
      const renderImage = () => {
        if (realImage.value) {
          return <t-image shape="round" {...realImage.value} />;
        }
        if (props.icon) {
          return renderTNodeJSX('icon');
        }
        return renderTNodeJSX('image');
      };

      return (
        <div
          class={gridItemClasses.value}
          style={rootStyle.value}
          v-hover={{ className: `${gridItemClass.value}--hover`, disabledHover: !hover.value }}
        >
          <div class={`${gridItemClass.value}__image ${gridItemClass.value}__image--${size.value}`}>
            {props.badge ? <t-badge {...(props.badge as Object)}>{renderImage()}</t-badge> : renderImage()}
          </div>

          <div class={`${gridItemClass.value}__content ${gridItemClass.value}__content--${props.layout}`}>
            <div class={`${gridItemClass.value}__title ${gridItemClass.value}__title--${size.value}`}>
              {renderTNodeJSX('text')}
            </div>
            <div class={`${gridItemClass.value}__description ${gridItemClass.value}__description--${props.layout}`}>
              {renderTNodeJSX('description')}
            </div>
          </div>
        </div>
      );
    };
  },
});
